import './holidays.scss';

import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  useCreateItem,
  useFetchList,
  usePatchItem,
} from 'services/common/react-query-common-service';
import { alertOptions } from 'utils/Config';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const Holidays = () => {
  const [startTime, setStartTime] = useState('09:30 AM');
  const [endTime, setEndTime] = useState('06:30 PM');
  const [workStartsOn, setWorkStartsOn] = useState('Monday');
  const [daysOff, setDaysOff] = useState({
    Friday: false,
    Monday: false,
    Saturday: true,
    Sunday: true,
    Thursday: false,
    Tuesday: false,
    Wednesday: false,
  });
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [editingHolidayId, setEditingHolidayId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    holidayId: string | null;
    holidayName: string;
  }>({
    holidayId: null,
    holidayName: '',
    open: false,
  });

  // React Query hooks
  const queryClient = useQueryClient();
  const createHolidayMutation = useCreateItem(false); // Disable default toast to use custom messages
  const patchHolidayMutation = usePatchItem(false); // For editing holidays with PATCH
  const deleteHolidayMutation = usePatchItem(false); // For deleting holidays (status update)

  // Fetch holidays from API
  const {
    data: holidaysData,
    isLoading,
    error,
  } = useFetchList(['holidays'], 'holidays', { toastShow: false });

  const handleDayOffChange = (day: string) => {
    setDaysOff((prev) => ({
      ...prev,
      [day]: !prev[day as keyof typeof prev],
    }));
  };

  const handleDeleteHoliday = (holiday: any) => {
    setDeleteConfirmation({
      holidayId: holiday.id,
      holidayName: holiday.holidayName,
      open: true,
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.holidayId) return;

    try {
      await deleteHolidayMutation.mutateAsync({
        data: { status: false },
        endpoint: `holidays/${deleteConfirmation.holidayId}/status`,
      });

      // Invalidate and refetch holidays query
      await queryClient.invalidateQueries({ queryKey: ['holidays'] });

      toast.success('Holiday deleted successfully!', alertOptions);

      // Close confirmation dialog
      setDeleteConfirmation({
        holidayId: null,
        holidayName: '',
        open: false,
      });
    } catch (error) {
      console.error('Failed to delete holiday:', error);
      setDeleteConfirmation({
        holidayId: null,
        holidayName: '',
        open: false,
      });
    }
  };

  const handleEditHoliday = (holiday: any) => {
    // Parse the date to extract month and day
    const dateParts = holiday.date?.split('-') || [];
    const monthNumber = dateParts[1] ? parseInt(dateParts[1], 10) : 0;
    const monthName =
      monthNumber > 0 && monthNumber <= 12 ? months[monthNumber - 1] : '';
    const day = dateParts[2] || '';

    // Populate form fields
    setSelectedMonth(monthName);
    setSelectedDate(day);
    setHolidayName(holiday.holidayName);
    setEditingHolidayId(holiday.id);
  };

  const handleSubmit = async () => {
    // Validate inputs
    if (!selectedMonth || !selectedDate || !holidayName.trim()) {
      toast.error('Please fill all fields', alertOptions);
      return;
    }

    // Get current year
    const currentYear = new Date().getFullYear();

    // Convert month name to month number (01-12)
    const monthIndex = months.indexOf(selectedMonth) + 1;
    const monthString = String(monthIndex).padStart(2, '0');

    // Format the date as YYYY-MM-DD
    const dateString = `${currentYear}-${monthString}-${selectedDate}`;

    // Prepare the payload
    const payload = {
      fullDate: dateString,
      holidayName: holidayName.trim(),
    };

    try {
      if (editingHolidayId) {
        // Update existing holiday with PATCH
        await patchHolidayMutation.mutateAsync({
          data: payload,
          endpoint: `holidays/${editingHolidayId}`,
        });
        toast.success('Holiday updated successfully!', alertOptions);
      } else {
        // Create new holiday
        await createHolidayMutation.mutateAsync({
          data: payload,
          endpoint: 'holidays',
        });
        toast.success('Holiday added successfully!', alertOptions);
      }

      // Invalidate and refetch holidays query
      await queryClient.invalidateQueries({ queryKey: ['holidays'] });

      // Reset form fields
      setSelectedMonth('');
      setSelectedDate('');
      setHolidayName('');
      setEditingHolidayId(null);
    } catch (error) {
      // Error is already handled by the mutation hook
      console.error('Failed to save holiday:', error);
    }
  };

  return (
    <Box className="holidays-container">
      <Typography className="page-title">Holiday Settings</Typography>

      <Box className="holidays-content">
        {/* Left Sidebar - Work Schedule */}
        <Box className="work-schedule-sidebar">
          <Box className="sidebar-header">
            <Typography className="section-title">Work Schedule</Typography>
          </Box>
          <Box className="sidebar-scrollable">
            {/* Work Hours */}
            <Box className="section-block">
              <Typography className="subsection-title">Work Hours</Typography>
              <Typography className="subsection-subtitle">
                Set the standard daily working hours.
              </Typography>
              <Box className="time-selectors">
                <FormControl size="small" className="time-select">
                  <Select
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}>
                    <MenuItem value="09:30 AM">09:30 AM</MenuItem>
                    <MenuItem value="10:00 AM">10:00 AM</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" className="time-select">
                  <Select
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}>
                    <MenuItem value="06:30 PM">06:30 PM</MenuItem>
                    <MenuItem value="07:00 PM">07:00 PM</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Work Starts On */}
            <Box className="section-block">
              <Typography className="subsection-title">
                Work starts on
              </Typography>
              <Typography className="subsection-subtitle">
                Select the first working day of the week.
              </Typography>
              <ToggleButtonGroup
                value={workStartsOn}
                exclusive
                onChange={(e, newValue) =>
                  newValue && setWorkStartsOn(newValue)
                }
                className="toggle-group">
                <ToggleButton value="Sunday" className="toggle-btn">
                  Sunday
                </ToggleButton>
                <ToggleButton value="Monday" className="toggle-btn">
                  Monday
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Weekly Days Off */}
            <Box className="section-block">
              <Typography className="subsection-title">
                Weekly Days Off
              </Typography>
              <Typography className="subsection-subtitle">
                Select weekly off days.
              </Typography>
              <Box className="days-off-grid">
                {Object.keys(daysOff).map((day) => (
                  <Box
                    key={day}
                    className={`day-checkbox ${
                      daysOff[day as keyof typeof daysOff] ? 'selected' : ''
                    }`}
                    onClick={() => handleDayOffChange(day)}>
                    <Typography className="day-label">{day}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Box className="sidebar-footer">
            {/* Save Changes Button */}
            <Button variant="contained" className="save-btn" fullWidth>
              Save Changes
            </Button>
          </Box>
        </Box>

        {/* Right Side - Independent Cards */}
        <Box className="holidays-right-column">
          {/* Add Holiday Controls Card */}
          <Box className="add-holiday-card">
            <Box className="add-holiday-controls">
              <FormControl size="small" className="control-select">
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  displayEmpty>
                  <MenuItem value="">Select month</MenuItem>
                  {months.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" className="control-select">
                <Select
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  displayEmpty>
                  <MenuItem value="">Select date</MenuItem>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => (
                    <MenuItem key={date} value={String(date).padStart(2, '0')}>
                      {String(date).padStart(2, '0')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Enter holiday name here"
                value={holidayName}
                onChange={(e) => setHolidayName(e.target.value)}
                className="holiday-input"
              />

              {editingHolidayId && (
                <Button
                  variant="outlined"
                  className="cancel-btn"
                  onClick={() => {
                    setSelectedMonth('');
                    setSelectedDate('');
                    setHolidayName('');
                    setEditingHolidayId(null);
                  }}
                  disabled={
                    createHolidayMutation.isPending ||
                    patchHolidayMutation.isPending
                  }>
                  Cancel
                </Button>
              )}

              <Button
                variant="contained"
                className="add-holiday-btn"
                onClick={handleSubmit}
                disabled={
                  createHolidayMutation.isPending ||
                  patchHolidayMutation.isPending
                }>
                {createHolidayMutation.isPending ||
                patchHolidayMutation.isPending
                  ? editingHolidayId
                    ? 'Updating...'
                    : 'Adding...'
                  : editingHolidayId
                  ? 'Edit Holiday'
                  : 'Add Holiday'}
              </Button>
            </Box>
          </Box>

          {/* Holidays Table Card */}
          <Box className="holidays-table-card">
            <TableContainer className="table-container">
              <Table stickyHeader className="holidays-table">
                <TableHead>
                  <TableRow>
                    <TableCell className="header-cell">S. No.</TableCell>
                    <TableCell className="header-cell">Date</TableCell>
                    <TableCell className="header-cell">Month</TableCell>
                    <TableCell className="header-cell">Holiday Name</TableCell>
                    <TableCell className="header-cell">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        className="body-cell">
                        Loading holidays...
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        className="body-cell">
                        Error loading holidays
                      </TableCell>
                    </TableRow>
                  ) : holidaysData?.data &&
                    Array.isArray(holidaysData.data) &&
                    holidaysData.data.length > 0 ? (
                    holidaysData.data.map((holiday: any, index: number) => {
                      // Parse the date (YYYY-MM-DD) to extract date and month
                      const dateParts = holiday.date?.split('-') || [];
                      const monthNumber = dateParts[1]
                        ? parseInt(dateParts[1], 10)
                        : 0;
                      const monthName =
                        monthNumber > 0 && monthNumber <= 12
                          ? months[monthNumber - 1]
                          : '';
                      const day = dateParts[2] || '';

                      return (
                        <TableRow
                          key={holiday.id || index}
                          className="table-row">
                          <TableCell className="body-cell">
                            {index + 1}
                          </TableCell>
                          <TableCell className="body-cell">{day}</TableCell>
                          <TableCell className="body-cell">
                            {monthName}
                          </TableCell>
                          <TableCell className="body-cell">
                            {holiday.holidayName}
                          </TableCell>
                          <TableCell className="body-cell">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditHoliday(holiday)}>
                              <EditOutlined fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteHoliday(holiday)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        className="body-cell">
                        No holidays found. Add your first holiday above!
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={() =>
          setDeleteConfirmation({
            holidayId: null,
            holidayName: '',
            open: false,
          })
        }
        PaperProps={{
          sx: {
            borderRadius: '8px',
            minWidth: '400px',
            padding: '8px',
          },
        }}>
        <Box sx={{ padding: '16px' }}>
          <Typography
            variant="h6"
            sx={{
              color: '#242424',
              fontFamily: 'Inter, sans-serif',
              fontSize: '18px',
              fontWeight: 600,
              marginBottom: '16px',
            }}>
            Delete Holiday
          </Typography>
          <Typography
            sx={{
              color: '#6c757d',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              marginBottom: '24px',
            }}>
            {`Are you sure you want to delete "${deleteConfirmation.holidayName}"?`}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}>
            <Button
              onClick={() =>
                setDeleteConfirmation({
                  holidayId: null,
                  holidayName: '',
                  open: false,
                })
              }
              sx={{
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                  borderColor: '#c1c7d0',
                },
                backgroundColor: '#fff',
                border: '1px solid #e1e4e8',
                borderRadius: '4px',
                color: '#2d2d2d',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                fontWeight: 600,
                padding: '8px 20px',
                textTransform: 'none',
              }}>
              No
            </Button>
            <Button
              onClick={confirmDelete}
              sx={{
                '&:hover': {
                  backgroundColor: '#c82333',
                },
                backgroundColor: '#dc3545',
                borderRadius: '4px',
                color: '#fff',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                fontWeight: 600,
                padding: '8px 20px',
                textTransform: 'none',
              }}>
              Yes, proceed
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
